/**
 * ScoreSheetCard — 2026-10-24 (per Gilles). The OFFICIAL score sheet: upload
 * screenshots of the league's game page (or a photo of the paper sheet), the
 * stats AI reads our team's goals, assists, penalties and the final; the
 * narrator checks the numbers and applies it. Applied, it is the final source
 * of truth for those stats (it overrides what the narration was heard to say,
 * and later AI re-runs keep it).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileCheck2, Loader2, Upload, RefreshCw, Trash2, Plus, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';

export const SHEET_ACCEPT = 'image/png,image/jpeg,image/webp';
const MAX_FILES = 50;
const TARGET_BYTES = 2.2 * 1024 * 1024;

function readAsDataUrl(blob) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
}

/** A big screenshot is shrunk (max 2400 px, JPEG); up to 50 per sheet (read in batches); text stays readable. */
async function shrink(file) {
  if (file.size <= TARGET_BYTES && ['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    return { data: await readAsDataUrl(file), mimeType: file.type };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const scale = Math.min(1, 2400 / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    for (const q of [0.9, 0.8, 0.7]) {
      const blob = await new Promise(r => c.toBlob(r, 'image/jpeg', q));
      if (blob && (blob.size <= TARGET_BYTES || q === 0.7)) return { data: await readAsDataUrl(blob), mimeType: 'image/jpeg' };
    }
    throw new Error('could not shrink');
  } finally { URL.revokeObjectURL(url); }
}

/**
 * Upload 1–50 screenshots of the official sheet; the server starts reading
 * them. 2026-10-28 — sent in PARTS of 6 (every hop in front of the API caps
 * a request at ~10 MB); the last part says `done` and the reading starts.
 */
export async function uploadScoreSheet(teamId, gameId, fileList) {
  const files = [...(fileList || [])].filter(f => /^image\//.test(f.type));
  if (!files.length) throw new Error('Pick screenshots (PNG, JPEG or WebP)');
  if (files.length > MAX_FILES) throw new Error(`Up to ${MAX_FILES} screenshots per game`);
  const images = [];
  for (const f of files) images.push(await shrink(f));
  const PART = 6;
  const first = images.slice(0, PART);
  const { data } = await api.post(`/teams/${teamId}/press-box/games/${gameId}/score-sheets`,
    { images: first, more: images.length > PART }, { timeout: 120000 });
  let sheet = data.sheet;
  for (let i = PART; i < images.length; i += PART) {
    const done = i + PART >= images.length;
    const { data: d2 } = await api.post(`/teams/${teamId}/press-box/games/${gameId}/score-sheets/${sheet.id}/images`,
      { images: images.slice(i, i + PART), done }, { timeout: 120000 });
    sheet = d2.sheet;
  }
  return sheet;
}

function SheetThumb({ teamId, gameId, sheetId, imageId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null; let dead = false;
    api.get(`/teams/${teamId}/press-box/games/${gameId}/score-sheets/${sheetId}/images/${imageId}`, { responseType: 'blob' })
      .then(r => { if (dead) return; url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { dead = true; if (url) URL.revokeObjectURL(url); };
  }, [teamId, gameId, sheetId, imageId]);
  if (!src) return <div style={{ width: 72, height: 96, borderRadius: 8, background: 'var(--surface-2)' }} />;
  return (
    <a href={src} target="_blank" rel="noreferrer" title="Open the screenshot">
      <img src={src} alt="Score sheet" style={{ width: 72, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
    </a>
  );
}

const num = { width: 58, padding: '4px 6px' };
const PERIODS = ['', '1', '2', '3', 'OT', 'SO'];

export default function ScoreSheetCard({ teamId, gameId, canEdit, onApplied }) {
  const [list, setList] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const draftFor = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/teams/${teamId}/press-box/games/${gameId}/score-sheets`);
      setList(data);
      const latest = data.sheets?.[0];
      if (latest?.status === 'ready' && latest.result && draftFor.current !== latest.id) {
        draftFor.current = latest.id;
        setDraft(JSON.parse(JSON.stringify(latest.result)));
      }
    } catch { setList({ sheets: [], error: true }); }
  }, [teamId, gameId]);
  useEffect(() => { load(); }, [load]);
  const latest = list?.sheets?.[0] || null;
  const working = latest && ['queued', 'reading'].includes(latest.status);
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [working, load]);

  const pick = () => fileRef.current?.click();
  const onFiles = async (files) => {
    setBusy(true);
    try {
      await uploadScoreSheet(teamId, gameId, files);
      draftFor.current = null; setDraft(null);
      toast.success('Score sheet uploaded — the stats AI is reading it');
      load();
    } catch (e) { toast.error(e.response?.data?.error || e.message || 'Could not upload the screenshots'); }
    finally { setBusy(false); }
  };
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'That did not work'); }
    finally { setBusy(false); }
  };

  if (!list || list.error) return null;
  if (!latest && !canEdit) return null;

  const setGoal = (i, k, v) => setDraft(d => ({ ...d, goals: d.goals.map((g, j) => (j === i ? { ...g, [k]: v } : g)) }));
  const setScorer = (i, n) => setGoal(i, 'scorer', { ...(draft.goals[i].scorer || {}), number: n });
  const setAssist = (i, k, n) => setDraft(d => ({ ...d, goals: d.goals.map((g, j) => {
    if (j !== i) return g;
    const a = [...(g.assists || [])];
    while (a.length <= k) a.push({ number: null, name: null });
    a[k] = { ...a[k], number: n };
    return { ...g, assists: a };
  }) }));
  const setPen = (i, k, v) => setDraft(d => ({ ...d, penalties: d.penalties.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const nOrNull = (v) => (v === '' ? null : v);
  const apply = () => act(async () => {
    const sheet = {
      ...draft,
      goals: draft.goals.map(g => ({ ...g, scorer: { ...g.scorer, number: nOrNull(g.scorer?.number) },
        assists: (g.assists || []).filter(a => a && a.number !== '' && a.number != null) })),
      penalties: draft.penalties.map(p => ({ ...p, player: { ...p.player, number: nOrNull(p.player?.number) }, minutes: Number(p.minutes) })),
      final: draft.final && draft.final.us !== '' && draft.final.us != null ? { us: Number(draft.final.us), them: Number(draft.final.them) } : null,
    };
    const { data } = await api.post(`/teams/${teamId}/press-box/games/${gameId}/score-sheets/${latest.id}/apply`, { sheet });
    const a = data.applied;
    toast.success(`Official sheet applied: ${a.goals.kept + a.goals.changed + a.goals.added} goals, ${a.assists.kept + a.assists.added} assists, ${a.penalties.added} penalties`);
    onApplied?.();
  });

  return (
    <div className="card mb-4" style={{ border: latest?.status === 'ready' ? '1px solid var(--gold)' : undefined }} data-score-sheet>
      <input ref={fileRef} type="file" accept={SHEET_ACCEPT} multiple style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files; e.target.value = ''; if (f?.length) onFiles(f); }} />
      <div className="flex items-center gap-2 font-bold mb-1">
        <FileCheck2 size={16} style={{ color: 'var(--gold)' }} /> Official score sheet
        {latest?.status === 'applied' && <span className="badge" style={{ background: 'var(--goal-a10)', color: 'var(--goal)', border: '1px solid var(--goal-a20)' }}><CheckCircle2 size={11} /> applied — the official stats</span>}
      </div>

      {!latest && (
        <div className="text-sm" style={{ color: 'var(--text-2)' }}>
          Upload screenshots of the league&rsquo;s game summary (or a photo of the paper sheet). The stats AI reads who scored, who
          assisted and the penalties — for this team only — and once you apply it, it is the final word on those stats.
          <div className="mt-2"><button type="button" className="btn-ice" disabled={busy} onClick={pick} data-sheet-upload>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload screenshots (up to 50)</button></div>
        </div>
      )}

      {latest && (
        <div className="flex items-start gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
          {latest.images.map(im => <SheetThumb key={im.id} teamId={teamId} gameId={gameId} sheetId={latest.id} imageId={im.id} />)}
        </div>
      )}

      {working && <div className="text-sm flex items-center gap-2" style={{ color: 'var(--ice)' }}><Loader2 size={13} className="animate-spin" />
        {latest.status === 'queued' && latest.error ? `Waiting for the GPU to read it (${latest.error.replace(/^the GPU is busy — /, '')})` : 'The stats AI is reading the sheet…'}</div>}

      {latest?.status === 'failed' && (
        <div className="text-sm" style={{ color: 'var(--penalty)' }}><AlertTriangle size={12} /> {latest.error || 'The sheet could not be read'}
          {canEdit && <button type="button" className="btn-ghost ml-2" style={{ padding: '4px 8px' }} disabled={busy}
            onClick={() => act(() => api.post(`/teams/${teamId}/press-box/games/${gameId}/score-sheets/${latest.id}/read`), 'Reading it again')}><RefreshCw size={12} /> Read again</button>}
        </div>
      )}

      {latest?.status === 'applied' && latest.summary && (
        <div className="text-sm" style={{ color: 'var(--text-2)' }}>
          Applied {latest.appliedAt ? new Date(latest.appliedAt).toLocaleString() : ''}: goals {latest.summary.goals.kept} confirmed,
          {' '}{latest.summary.goals.changed} re-credited, {latest.summary.goals.added} added, {latest.summary.goals.discarded} removed;
          {' '}assists and penalties are the sheet&rsquo;s{latest.summary.final ? `; final ${latest.summary.final.us}–${latest.summary.final.them}` : ''}.
          {canEdit && <div className="mt-2"><button type="button" className="btn-ghost" style={{ padding: '4px 10px' }} disabled={busy} onClick={pick}><Upload size={12} /> Upload a corrected sheet</button></div>}
        </div>
      )}

      {latest?.status === 'ready' && draft && (
        <div data-sheet-review>
          <div className="text-sm mb-2" style={{ color: 'var(--text-2)' }}>
            {draft.our_team_found === false
              ? <span style={{ color: 'var(--puck)' }}><AlertTriangle size={12} /> The AI could not tell which side of the sheet is this team — check every number before applying.</span>
              : <>Read{draft.our_team_as_printed ? <> as <strong>{draft.our_team_as_printed}</strong></> : ''}. Check the jersey numbers against the screenshots, fix any misread, then apply.</>}
          </div>
          <div className="flex items-center gap-2 mb-3 text-sm">
            <span className="label" style={{ margin: 0 }}>Final</span>
            <label className="text-xs" style={{ color: 'var(--text-3)' }}>Us <input type="number" min="0" max="99" className="input-field" style={num} disabled={!canEdit}
              value={draft.final?.us ?? ''} onChange={(e) => setDraft(d => ({ ...d, final: { ...(d.final || { them: '' }), us: e.target.value } }))} /></label>
            <label className="text-xs" style={{ color: 'var(--text-3)' }}>Them <input type="number" min="0" max="99" className="input-field" style={num} disabled={!canEdit}
              value={draft.final?.them ?? ''} onChange={(e) => setDraft(d => ({ ...d, final: { ...(d.final || { us: '' }), them: e.target.value } }))} /></label>
          </div>

          <div className="text-xs font-bold mb-1" style={{ color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Our goals</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="text-sm" style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
              <thead><tr style={{ color: 'var(--text-3)', fontSize: 11 }}>
                <th style={{ textAlign: 'left', padding: '2px 6px' }}>Per.</th><th style={{ textAlign: 'left', padding: '2px 6px' }}>Time</th>
                <th style={{ textAlign: 'left', padding: '2px 6px' }}>Scorer #</th><th style={{ textAlign: 'left', padding: '2px 6px' }}>Assist #</th>
                <th style={{ textAlign: 'left', padding: '2px 6px' }}>Assist #</th><th />
              </tr></thead>
              <tbody>
                {draft.goals.map((g, i) => (
                  <tr key={i} data-sheet-goal={i}>
                    <td style={{ padding: '2px 6px' }}><select className="input-field" style={{ ...num, width: 64 }} disabled={!canEdit} value={g.period || ''} onChange={(e) => setGoal(i, 'period', e.target.value || null)}>
                      {PERIODS.map(p => <option key={p} value={p}>{p || '–'}</option>)}</select></td>
                    <td style={{ padding: '2px 6px' }}><input className="input-field" style={{ ...num, width: 66 }} disabled={!canEdit} value={g.time || ''} placeholder="mm:ss" onChange={(e) => setGoal(i, 'time', e.target.value || null)} /></td>
                    <td style={{ padding: '2px 6px' }} title={g.scorer?.name || ''}><input type="number" className="input-field" style={num} disabled={!canEdit} value={g.scorer?.number ?? ''} onChange={(e) => setScorer(i, e.target.value)} />
                      {g.scorer?.name && <span className="text-xs ml-1" style={{ color: 'var(--text-3)' }}>{g.scorer.name}</span>}</td>
                    {[0, 1].map(k => (
                      <td key={k} style={{ padding: '2px 6px' }} title={g.assists?.[k]?.name || ''}><input type="number" className="input-field" style={num} disabled={!canEdit} value={g.assists?.[k]?.number ?? ''} onChange={(e) => setAssist(i, k, e.target.value)} />
                        {g.assists?.[k]?.name && <span className="text-xs ml-1" style={{ color: 'var(--text-3)' }}>{g.assists[k].name}</span>}</td>
                    ))}
                    <td>{canEdit && <button type="button" className="btn-ghost" style={{ padding: '2px 6px' }} title="Not our goal — remove" onClick={() => setDraft(d => ({ ...d, goals: d.goals.filter((_, j) => j !== i) }))}><X size={12} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canEdit && <button type="button" className="btn-ghost mb-3" style={{ padding: '4px 10px' }} onClick={() => setDraft(d => ({ ...d, goals: [...d.goals, { period: null, time: null, scorer: { number: '', name: null }, assists: [], type: null }] }))}><Plus size={12} /> Add a goal</button>}

          <div className="text-xs font-bold mb-1" style={{ color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Our penalties</div>
          {draft.penalties.length === 0 && <div className="text-xs mb-2" style={{ color: 'var(--text-3)' }}>None read.</div>}
          {draft.penalties.map((p, i) => (
            <div key={i} className="flex items-center gap-2 mb-1 text-sm" style={{ flexWrap: 'wrap' }} data-sheet-penalty={i}>
              <select className="input-field" style={{ ...num, width: 64 }} disabled={!canEdit} value={p.period || ''} onChange={(e) => setPen(i, 'period', e.target.value || null)}>
                {PERIODS.map(x => <option key={x} value={x}>{x || '–'}</option>)}</select>
              <input type="number" className="input-field" style={num} disabled={!canEdit} placeholder="#" value={p.player?.number ?? ''} onChange={(e) => setPen(i, 'player', { ...(p.player || {}), number: e.target.value })} />
              <input type="number" className="input-field" style={num} disabled={!canEdit} value={p.minutes ?? ''} onChange={(e) => setPen(i, 'minutes', e.target.value)} /><span className="text-xs" style={{ color: 'var(--text-3)' }}>min</span>
              <span className="text-xs" style={{ color: 'var(--text-2)' }}>{p.infraction || ''}{p.player?.name ? ` — ${p.player.name}` : ''}</span>
              {canEdit && <button type="button" className="btn-ghost" style={{ padding: '2px 6px' }} onClick={() => setDraft(d => ({ ...d, penalties: d.penalties.filter((_, j) => j !== i) }))}><X size={12} /></button>}
            </div>
          ))}
          {canEdit && <button type="button" className="btn-ghost mb-3" style={{ padding: '4px 10px' }} onClick={() => setDraft(d => ({ ...d, penalties: [...d.penalties, { period: null, time: null, player: { number: '' }, minutes: 2, infraction: null }] }))}><Plus size={12} /> Add a penalty</button>}

          {draft.notes?.length > 0 && <div className="text-xs mb-2" style={{ color: 'var(--puck)' }}>{draft.notes.join(' · ')}</div>}
          {canEdit && (
            <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
              <button type="button" className="btn-ice" disabled={busy} onClick={() => {
                if (window.confirm('Apply the official sheet? Our goals, assists and penalties for this game become exactly the sheet\'s (goals that match keep their audio), and its final becomes the final score.')) apply();
              }} data-sheet-apply>{busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Apply as the official stats</button>
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={busy}
                onClick={() => act(async () => { draftFor.current = null; setDraft(null); await api.post(`/teams/${teamId}/press-box/games/${gameId}/score-sheets/${latest.id}/read`); }, 'Reading it again')}><RefreshCw size={12} /> Read again</button>
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={busy}
                onClick={() => { if (window.confirm('Discard this score sheet?')) act(async () => { draftFor.current = null; setDraft(null); await api.delete(`/teams/${teamId}/press-box/games/${gameId}/score-sheets/${latest.id}`); }, 'Discarded'); }}><Trash2 size={12} /> Discard</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
